import invert from 'lodash/invert';
import identity from 'lodash/identity';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isNumber from 'lodash/isNumber';

import {isCohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {DATASET_MIXPANEL} from 'common/report/constants';
import {CohortsSection, GroupSection} from 'common/report/insights/models/section';
import {InsightsReportSectionsOverride} from 'common/report/insights/models/types';
import {reporter} from 'common/report/error-reporting';
import {FILTER_VALUE_ATTRIBUTES} from 'common/widgets/property-filter-menu/models/property-filter';
import {isMultiAttributionSegmentation} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {useReportConsistency, useSingleReport} from 'common/report/util/feature-sampling';
import {Segmentation} from 'common/types/reports/query';

import {CohortsClause} from '../models/cohorts-clause';
import {GroupClause} from '../models/group-clause';
import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from '../models/constants';
import {GroupClause as GroupClauseType, InsightsResourceType} from './types';

/**
 * Transforms Cohorts and Group Sections into Segment
 * @param {bool} renameResourceTypes whether to rename "people" to "users", etc for resource types
 */
export function getSelectedSegments(groupClauses: GroupClauseType[], renameResourceTypes = true): Segment[] {
  const convertGroupClause = (clause) => {
    if (clause instanceof CohortsClause) {
      return {cohort: pick(clause, [`id`, `name`, `negated`, `raw_cohort`, `data_group_id`])};
    } else if (clause.clauses) {
      // TODO: Remove value comparison
      return clause.clauses.map((c) => convertGroupClause(c));
    } else if (clause instanceof GroupClause) {
      if (clause.isGroupByCohorts()) {
        const cohorts = clause.cohorts;
        return cohorts.map((c) => ({
          cohort: pick(c, [`id`, `name`, `negated`, `raw_cohort`]),
        }));
      }
      let resourceType = clause.resourceType;
      if (renameResourceTypes) {
        resourceType = invert(QUERY_BUILDER_RESOURCE_TYPE_MAPPING)[clause.resourceType] as InsightsResourceType;
      }
      let basePropsKeys = [
        `behavior`,
        `tempDataGroupId`,
        `joinPropertyType`,
        `customBucket`,
        `customGroups`,
        `unit`,
        `dataGroupId`,
        `customProperty`,
        `customPropertyId`,
        `propertyObjectKey`,
        `listQuantifier`,
        `listItemGroup`,
        `profileHistoryOptions`,
        `scdEvent`,
      ];

      // Support for Value Comparison
      if (Object.prototype.hasOwnProperty.call(clause, `filterOperator`)) {
        basePropsKeys = basePropsKeys.concat(FILTER_VALUE_ATTRIBUTES);
      }
      const baseProps = pickBy(pick(clause, basePropsKeys), identity);

      // if group clause contains filterOperator, we treat it as a group_by clause that requires filter attributes
      // Need to override filterOperator and filterValue to render the query entry section correctly
      if (baseProps.filterOperator) {
        baseProps[`filterOperator`] = clause.propertyFilter.filterOperator;
        baseProps[`filterValue`] = clause.propertyFilter.filterValue;
      }
      if (isNumber(clause.startStepNumber) || clause.startStepNumber === null) {
        baseProps.startStepNumber = clause.startStepNumber;
      }
      if (isNumber(clause.endStepNumber) || clause.endStepNumber === null) {
        baseProps.endStepNumber = clause.endStepNumber;
      }

      return {
        [resourceType]: {
          ...baseProps,
          resourceType,
          propertyName: clause.value,
          propertyType: clause.typeCast || clause[`filterType`] || clause.propertyType,
          ...(clause.propertyType ? {propertyDefaultType: clause.propertyType} : {}),
        },
      };
    } else {
      reporter.error(`Clause is neither of the type GroupClause or CohortsClause`);
      return null;
    }
  };

  return [...groupClauses.map((clause) => convertGroupClause(clause))];
}

/**
 * Transforms Segments into Cohorts and Group Sections
 */
export function getSegmentSections(segments: Segment[]): [CohortsSection, GroupSection] {
  const groupClauses = [];
  const multiAttributionClauses = [];

  segments.forEach((segment) => {
    if (isCohortSegment(segment)) {
      // Support multiple segments of cohort values
      const groupByCohorts = segment.map((item) => ({...item.cohort}));
      const groupClause = new GroupClause({
        cohorts: groupByCohorts,
        value: groupByCohorts.map((cohort) => cohort.name),
      });
      if (useSingleReport() || useReportConsistency()) {
        groupClauses.push(groupClause);
      } else {
        // Place cohorts at the top level in group section
        if (groupClauses[0] && groupClauses[0].isGroupByCohorts()) {
          groupClauses[0] = groupClause;
        } else {
          groupClauses.unshift(groupClause);
        }
      }
      return;
    }

    const groupBy: Segmentation = Object.values(segment)[0];

    const getClauseAttrs = (groupBy: Segmentation) => {
      const baseProps = pick(
        groupBy,
        `behavior`,
        `dataGroupId`,
        `customPropertyId`,
        `customProperty`,
        `joinPropertyType`,
        `tempDataGroupId`,
        `propertyObjectKey`,
        `unit`,
        `customBucket`,
        `customGroups`,
        `listQuantifier`,
        `profileHistoryOptions`,
        `startStepNumber`,
        `endStepNumber`,
        `scdEvent`,
      );

      return {
        ...baseProps,
        ...(groupBy.propertyType !== groupBy.propertyDefaultType ? {typeCast: groupBy.propertyType} : {}),
        dataset: DATASET_MIXPANEL,
        dataGroupId: groupBy.dataGroupId,
        listItemGroup: groupBy.listItemGroup as any,
        propertyType: groupBy.propertyDefaultType,
        resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[groupBy.resourceType],
        value: groupBy.propertyName,
        // Cleanup: This is due to differences between Segmentation and actual
        // Segmentation values in Insights via: ResourceType/InsightsResourceType and TimeUnit/TimeUnit | SpecialTimeUnit.
      } as GroupClauseType;
    };

    const groupClause = new GroupClause(getClauseAttrs(groupBy));
    if (isMultiAttributionSegmentation(groupBy)) {
      multiAttributionClauses.push(groupClause);
    } else {
      groupClauses.push(groupClause);
    }
  });

  return [
    new CohortsSection([]),
    new GroupSection([
      ...groupClauses,
      // multi-attribution properties should always inner-most breakdowns
      ...multiAttributionClauses,
    ]),
  ];
}

export function getSegmentSectionsOverride(segments: Segment[]): InsightsReportSectionsOverride {
  const [cohortsSection, groupSection] = getSegmentSections(segments);
  return {
    cohorts: cohortsSection.toBookmarkData(),
    group: groupSection.toBookmarkData(),
  };
}
